import * as React from 'react';

import { type UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { actionMessages } from 'src/features/parkingActions/utils/messages';
import { PointActionType } from 'src/features/points/types';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/utils/messages';

import type { PointActionFormSchemaValues } from 'src/features/points/api/usePointAction';
import type { PointType, Point, Reason } from 'src/features/points/types';

type BarrierActionFormProps = {
    form: UseFormReturn<PointActionFormSchemaValues>;
    points?: Point[];
    reasons?: Reason[];
    actionType?: PointActionType;
    onSubmit: (values: PointActionFormSchemaValues) => void;
    selectedPointState: [PointType | undefined, React.Dispatch<React.SetStateAction<PointType | undefined>>];
};

function BarrierActionForm({
    form,
    points,
    reasons,
    actionType,
    selectedPointState,
    onSubmit,
}: BarrierActionFormProps) {
    const [selectedPointType, setSelectedPointType] = selectedPointState;
    const { formatMessage } = useIntl();
    return (
        <Form form={form} variant="dialog" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
                control={form.control}
                name="pointId"
                render={({ field }) => {
                    const onChange = (value: string) => {
                        const pointType = points?.find((point) => point.id === value)?.type;
                        setSelectedPointType(pointType);
                        field.onChange(value);
                    };
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(generalMessages.location)}
                            </FormLabel>
                            {points ? (
                                <FormControlStack>
                                    <FormControl>
                                        <Select onValueChange={onChange} defaultValue={field.value}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue
                                                    placeholder={formatMessage(actionMessages.pointIdPlaceholder)}
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {points.map((point) => (
                                                    <SelectItem key={point.id} value={point.id}>
                                                        {point.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage className="font-normal" />
                                </FormControlStack>
                            ) : (
                                <Skeleton className="h-10 w-full" />
                            )}
                        </FormItem>
                    );
                }}
            />
            {actionType === PointActionType.NORMAL && (
                <FormField
                    control={form.control}
                    name="reason"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(generalMessages.reason)}
                            </FormLabel>
                            {reasons ? (
                                <FormControlStack>
                                    <FormControl>
                                        <Select
                                            disabled={!selectedPointType}
                                            onValueChange={(value) => field.onChange(value.toString())}
                                            defaultValue={field.value}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue
                                                    placeholder={formatMessage(actionMessages.reasonPlaceholder)}
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {selectedPointType &&
                                                    reasons
                                                        .filter((reason) => reason.use_for === selectedPointType)
                                                        .map((reason) => (
                                                            <SelectItem
                                                                key={`${reason.id}..${reason.use_for}`}
                                                                value={`${reason.id}..${reason.use_for}`}
                                                            >
                                                                {reason.description}
                                                            </SelectItem>
                                                        ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage className="font-normal" />
                                </FormControlStack>
                            ) : (
                                <Skeleton className="h-10 w-full" />
                            )}
                        </FormItem>
                    )}
                />
            )}
        </Form>
    );
}

export default BarrierActionForm;
